import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { Minimize2, Send, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import { useAuth } from "../../../../Reusable/AuthProvider";
import { useChat } from "../../../../Reusable/UseUserChat";
import { useWebSocket } from "../../../../Reusable/useWebSocket";
import {
  getChatOtherUser,
  getChatOtherUserFullName,
} from "../../../../Reusable/Utils";

interface ChatBoxProps {
  usersKeyOrCertificationId: string;
}

export default function ChatBox({ usersKeyOrCertificationId }: ChatBoxProps) {
  const markAllMessagesAsRead = () => {
    // Vérifier s'il y a au moins un message non lu envoyé par quelqu'un d'autre
    const unreadFromOther = messages.some(
      (msg) => msg.unread && msg.sender.id !== user?.id
    );
    if (!unreadFromOther) return;
    AxiosHttpClient.put(
      "api/v1/docuveritas/user-conversations/messages/read/" +
        usersKeyOrCertificationId
    ).then((res) => {
      fetchMessages(usersKeyOrCertificationId, 0, true); // force refresh
      //setPage(1);
      //setHasMore(true);
    });
  };

  const {
    minimizeChat,
    closeChat,
    sendMessage,
    conversationCache,
    messageCache,
    fetchMessages,
    setMessageCache,
  } = useChat();
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const conversation = conversationCache[usersKeyOrCertificationId];
  const cache = messageCache[usersKeyOrCertificationId];
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(cache?.page ?? 0);
  const [hasMore, setHasMore] = useState(cache?.hasMore ?? true);
  const messages = cache?.messages ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const subscribedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null); // Ajout du ref

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!cache) {
      fetchMessages(usersKeyOrCertificationId, 0);
    }
  }, [usersKeyOrCertificationId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleScroll = () => {
    if (scrollRef.current && scrollRef.current.scrollTop === 0 && hasMore) {
      const nextPage = page + 1;
      fetchMessages(usersKeyOrCertificationId, nextPage).then(() => {
        setPage(nextPage);
        setHasMore(messageCache[usersKeyOrCertificationId]?.hasMore ?? false);
      });
    }
  };

  if (!conversation) {
    return null;
  }

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      sendMessage(usersKeyOrCertificationId, message);
      setMessageCache((prev) => {
        const old = prev[usersKeyOrCertificationId]?.messages || [];
        return {
          ...prev,
          [usersKeyOrCertificationId]: {
            messages: [
              ...old,
              {
                id: `temp-${Date.now()}`,
                content: message,
                unread: false,
                createdAt: dayjs(),
                sender: user!,
                conversation: conversation!,
              },
            ],
            hasMore: prev[usersKeyOrCertificationId]?.hasMore ?? true,
            page: prev[usersKeyOrCertificationId]?.page ?? 0,
          },
        };
      });
      setMessage("");
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        height: 450,
        display: "flex",
        flexDirection: "column",
        borderRadius: (theme) =>
          `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        overflow: "hidden",
        bgcolor: "hsl(var(--card))",
        color: "hsl(var(--foreground))",
        border: "1px solid hsl(var(--border))",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: "hsl(var(--muted))",
          borderBottom: "1px solid hsl(var(--border))",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            src={getChatOtherUser(conversation, user?.id).imageUrl}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              color: "hsl(var(--primary-foreground))",
              bgcolor: "hsl(var(--primary))",
              border: "1px solid hsl(var(--border))",
            }}
          />
          <Box
            component="span"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "hsl(var(--foreground))",
            }}
          >
            {getChatOtherUserFullName(conversation, user?.id)}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => minimizeChat(usersKeyOrCertificationId)}
            sx={{
              color: "hsl(var(--muted-foreground))",
              "&:hover": {
                bgcolor: "hsl(var(--accent))",
                color: "hsl(var(--accent-foreground))",
              },
            }}
          >
            <Minimize2 size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => closeChat(usersKeyOrCertificationId)}
            sx={{
              color: "hsl(var(--muted-foreground))",
              "&:hover": {
                bgcolor: "hsl(var(--destructive))",
                color: "hsl(var(--destructive-foreground))",
              },
            }}
          >
            <X size={16} />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          height: 300,
          p: 1.5,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          bgcolor: "hsl(var(--background))",
        }}
      >
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              maxWidth: "80%",
              borderRadius: 2,
              p: 1.5,
              bgcolor:
                msg.sender.id === user?.id
                  ? "hsl(var(--primary))"
                  : "hsl(var(--muted))",
              color:
                msg.sender.id === user?.id
                  ? "hsl(var(--primary-foreground))"
                  : "hsl(var(--foreground))",
              ml: msg.sender.id === user?.id ? "auto" : 0,
              border: "1px solid hsl(var(--border))",
            }}
          >
            {msg.content}
          </Box>
        ))}
      </Box>

      <Box
        component="form"
        onSubmit={handleSend}
        sx={{
          p: 1.5,
          borderTop: "1px solid hsl(var(--border))",
          bgcolor: "hsl(var(--card))",
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <TextField
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          variant="outlined"
          size="small"
          fullWidth
          inputRef={inputRef}
          onFocus={markAllMessagesAsRead}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
              "& fieldset": {
                borderColor: "hsl(var(--border))",
              },
              "&:hover fieldset": {
                borderColor: "hsl(var(--accent))",
              },
              "&.Mui-focused fieldset": {
                borderColor: "hsl(var(--primary))",
              },
            },
            "& .MuiInputBase-input": {
              color: "hsl(var(--foreground))",
              "&::placeholder": {
                color: "hsl(var(--muted-foreground))",
                opacity: 1,
              },
            },
          }}
        />
        <IconButton
          type="submit"
          size="small"
          sx={{
            color: "hsl(var(--muted-foreground))",
            "&:hover": {
              bgcolor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
            },
          }}
        >
          <Send size={16} />
        </IconButton>
      </Box>
    </Paper>
  );
}
